import { ReactNode, useMemo } from 'react';
import { CaptivePortalDataKeys, CaptivePortalFormData, CaptivePortalTemplateProps } from '../types';
import Box from '@mui/material/Box';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { CaptivePortalTemplateData } from '@lib/model/captive-portal-template-data-model';

export function CaptivePortalContainer<Keys extends CaptivePortalDataKeys>(
  props: CaptivePortalTemplateProps<Keys> & { children: ReactNode },
) {
  const data = props.data as CaptivePortalTemplateData;
  const currentTheme = useTheme();

  const getFontFamily = () => {
    const fontFamily = data.fontFamily;
    if (!fontFamily || fontFamily === 'Default Font' || fontFamily === '') {
      return currentTheme.typography.fontFamily;
    }

    // Return font family with fallbacks
    switch (fontFamily) {
      case 'Inter':
        return '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      case 'Montserrat':
        return '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      default:
        return fontFamily;
    }
  };

  // Create a custom theme with the selected font family using useMemo for performance
  const customTheme = useMemo(() => {
    const selectedFont = getFontFamily();
    console.log('Selected font family:', selectedFont);
    console.log('Font family from props:', data.fontFamily);

    return createTheme({
      ...currentTheme,
      typography: {
        ...currentTheme.typography,
        fontFamily: selectedFont,
        // Override all typography variants to use the selected font
        h1: { ...currentTheme.typography.h1, fontFamily: selectedFont },
        h2: { ...currentTheme.typography.h2, fontFamily: selectedFont },
        h3: { ...currentTheme.typography.h3, fontFamily: selectedFont },
        h4: { ...currentTheme.typography.h4, fontFamily: selectedFont },
        h5: { ...currentTheme.typography.h5, fontFamily: selectedFont },
        h6: { ...currentTheme.typography.h6, fontFamily: selectedFont },
        body1: { ...currentTheme.typography.body1, fontFamily: selectedFont },
        body2: { ...currentTheme.typography.body2, fontFamily: selectedFont },
        button: { ...currentTheme.typography.button, fontFamily: selectedFont },
        caption: { ...currentTheme.typography.caption, fontFamily: selectedFont },
        overline: { ...currentTheme.typography.overline, fontFamily: selectedFont },
      },
      components: {
        ...currentTheme.components,
        // Override Material-UI component typography
        MuiTypography: {
          styleOverrides: {
            root: {
              fontFamily: selectedFont,
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiInputBase-input': {
                fontFamily: selectedFont,
              },
              '& .MuiInputLabel-root': {
                fontFamily: selectedFont,
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontFamily: selectedFont,
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            label: {
              fontFamily: selectedFont,
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            root: {
              fontFamily: selectedFont,
            },
          },
        },
      },
    });
  }, [currentTheme, data.fontFamily]);

  return (
    <ThemeProvider theme={customTheme}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          backgroundColor: data.backgroundColor || 'transparent',
          fontFamily: getFontFamily(),
        }}
      >
        {props.children}
      </Box>
    </ThemeProvider>
  );
}

export const isCaptivePortalFormDataValid = (
  data: Partial<CaptivePortalTemplateData>,
  formData: CaptivePortalFormData,
): boolean => {
  // Check required fields
  if (data.requireEmail && (!formData.email || !formData.email.trim())) {
    return false;
  }
  if (data.requireName) {
    if (!formData.firstName || !formData.firstName.trim()) {
      return false;
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      return false;
    }
  }
  // Check required marketing acceptance
  if (data.requireAcceptMarketing && !formData.acceptedMarketing) {
    return false;
  }

  if (data.termsAndConditionsUrl && !formData.acceptedTerms) {
    return false;
  }

  // Check email format if email is provided
  if (formData.email && formData.email.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return false;
    }
  }
  return true;
};
